import { format } from "date-fns";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "../../context";
import LoadingSpinner from "../LoadingSpinner";
import { markAttendance } from "../../context/actions/attendance";

function CourseCard({ course, liveSession, nextSession, hasStarted, isNG }) {
  const navigate = useNavigate();
  const isLive =
    course.course_type === "live" ||
    course.course_type === "Live" ||
    course.course_type_id === 1;

  const handleAction = () => {
    if (liveSession?.link) {
      window.open(liveSession.link, "_blank");
      if (liveSession.id) markAttendance(liveSession.id); // silent — falls back to the Mark Attendance button in the blackboard
    } else if (isLive) {
      navigate(`/dashboard/course/${course.uuid}`);
    } else {
      navigate(`/course-video/${course.uuid}`);
    }
  };

  const buttonLabel = liveSession
    ? "Join Class Now"
    : isLive
    ? hasStarted ? "Continue Learning" : "Start Course"
    : hasStarted ? "Continue Learning" : "Start Course";

  const rawDate = isNG ? nextSession?.time?.wat?.date : nextSession?.time?.est?.date;
  const formattedDate = rawDate ? format(new Date(rawDate), "EEE, MMM d @ h:mm a") : null;
  const timezoneSuffix = isNG ? "WAT" : "EST";

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-xl hover:border-gray-200/80 hover:-translate-y-1 transition-all duration-300 flex flex-col h-full">
      {/* Thumbnail */}
      <div className="relative h-44 bg-gradient-to-br from-[#d15424]/10 to-[#294a93]/10 overflow-hidden flex-shrink-0">
        {course.image_url ? (
          <img
            src={course.image_url}
            alt={course.title}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-5xl bg-slate-50">📖</div>
        )}

        {liveSession && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-red-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-lg shadow-red-500/25 animate-pulse">
            <span className="w-1.5 h-1.5 bg-white rounded-full" />
            LIVE NOW
          </div>
        )}

        <div className={`absolute top-3 right-3 text-white text-[10px] font-bold px-2.5 py-1 rounded-full capitalize backdrop-blur-md shadow-sm ${
          isLive ? "bg-emerald-600/90" : "bg-blue-600/90"
        }`}>
          {isLive ? "Live Cohort" : "Self-Paced"}
        </div>
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col flex-grow">
        <h3 className="font-bold text-gray-800 text-base leading-snug mb-3 line-clamp-2 min-h-[2.5rem]">
          {course.title}
        </h3>

        {nextSession && !liveSession && (
          <div className="mb-4 bg-orange-50/50 border border-orange-100/60 rounded-xl p-3 flex flex-col gap-0.5">
            <span className="text-[9px] font-extrabold tracking-wider text-[#d15424] uppercase">Next Class Schedule</span>
            <span className="text-xs font-semibold text-gray-800 truncate" title={nextSession.topic}>{nextSession.topic}</span>
            {formattedDate && (
              <span className="text-[11px] text-gray-600 flex items-center gap-1 mt-1 font-medium">
                <span className="opacity-80">📅</span> {formattedDate} ({timezoneSuffix})
              </span>
            )}
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={handleAction}
          className={`mt-auto w-full py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 shadow-sm ${
            liveSession
              ? "bg-red-500 hover:bg-red-600 text-white hover:shadow-red-500/10 hover:shadow-md"
              : "bg-[#d15424] hover:bg-[#b8451d] text-white hover:shadow-orange-500/10 hover:shadow-md active:scale-[0.98]"
          }`}
        >
          {buttonLabel}
        </button>
      </div>
    </div>
  );
}

export default function CourseBlackboard() {
  const navigate = useNavigate();
  const { userCoursesState, scheduleState, userLocation } = useContext(GlobalContext);

  const courses = userCoursesState?.data || [];
  const schedule = scheduleState?.data || [];
  const isNG = userLocation?.data === "NG";

  const findLiveSession = (uuid) =>
    schedule.find(
      (s) => s.status === "live" && s.courses?.some((c) => c.uuid === uuid)
    ) || null;

  const findNextSession = (uuid) =>
    schedule.find(
      (s) => s.status === "upcoming" && s.courses?.some((c) => c.uuid === uuid)
    ) || null;

  // Course has started if any session is recorded or ended
  const courseHasStarted = (uuid) =>
    schedule.some(
      (s) =>
        (s.status === "recorded" || s.status === "ended") &&
        s.courses?.some((c) => c.uuid === uuid)
    );

  const isLiveCourse = (course) =>
    course.course_type === "live" ||
    course.course_type === "Live" ||
    course.course_type_id === 1;

  if (userCoursesState?.loading && courses.length === 0) {
    return <LoadingSpinner />;
  }

  if (courses.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
        <div className="text-5xl mb-3">📚</div>
        <h3 className="font-semibold text-gray-700 mb-1">No courses yet</h3>
        <p className="text-sm text-gray-400 mb-4">
          Enroll in a cohort to get started
        </p>
        <button
          onClick={() => navigate("/cohorts")}
          className="bg-[#d15424] text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#b8451d] transition-colors"
        >
          Browse Cohorts
        </button>
      </div>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {courses.map((course, i) => (
        <CourseCard
          key={course.uuid || i}
          course={course}
          liveSession={isLiveCourse(course) ? findLiveSession(course.uuid) : null}
          nextSession={isLiveCourse(course) ? findNextSession(course.uuid) : null}
          hasStarted={courseHasStarted(course.uuid)}
          isNG={isNG}
        />
      ))}
    </div>
  );
}
